import React, { ReactChild, ReactNode } from "react";
import { ButtonIconProps, ButtonWeightProps } from "../components/core/Button/Button";
import { createTemporaryId } from "../utils/model";
import { TelephonyContextType } from "../components/telephony/TelephonyContext";
import { text } from "../utils/i18n";

type AsynchronousNotificationClickParams = {
  telephonyContext: TelephonyContextType;

  /**
   * This notification.
   */
  notification: AsynchronousNotification;
}

export type ANIconWeight = "normal" | "danger";

/**
 * Represents a notification which can appear asynchronously.
 */
export class AsynchronousNotification {
  readonly id: string = createTemporaryId();
  readonly key: string = createTemporaryId();
  readonly tags: string[] = [];
  readonly createdAt: Date;
  readonly type: AsynchronousNotificationType = AsynchronousNotificationType.Custom;
  readonly weight: AsynchronousNotificationWeight = AsynchronousNotificationWeight.Normal;
  readonly title: string;
  readonly body: ReactChild | (() => ReactChild | null) = text`Ingen text`;
  /**
   * Optional document.title override.
   */
  readonly documentTitle?: string;
  readonly icon: { name: string; type?: string; weight?: ANIconWeight } = { name: "info", type: 'solid' };
  readonly isRead: boolean = false;
  readonly isDismissable: boolean = true;

  /**
   * Interval in milliseconds to call `body`. `body` must be a function
   * for this to take effect. 
   */
  readonly bodyUpdateInterval?: number;
  readonly actions = { confirm: null, dismiss: null, default: null } as {
    dismiss?: Optional<AsynchronousNotificationAction, 'children'> | null;
    confirm?: AsynchronousNotificationAction | null;

    /**
     * Called when the entire notification is clicked.
     */
    default?: (Pick<AsynchronousNotificationAction, 'onClick' | 'weight'> & { chevron?: boolean; }) | null;
  };

  constructor(base?: Partial<AsynchronousNotification>) {
    if (base) {
      Object.assign(this, base);
    }

    if (!this.body) {
      this.body = "";
    }

    this.title = this.title || text`Ingen rubrik`; // Title is mandatory.
  }

  read() {
    return new AsynchronousNotification({
      ...this,
      isRead: true
    });
  }

  get hasAction() {
    return Boolean(this.actions.confirm || this.actions.dismiss);
  }

  /**
   * Returns an identical notification which can be displayed again.
   */
  clone() {
    return new AsynchronousNotification({
      ...this,
      id: createTemporaryId(),
      key: createTemporaryId(),
      isRead: false
    });
  }
}

export enum AsynchronousNotificationType {
  Custom = 'custom',
  IncomingCall = 'incomingCall'
}

export enum AsynchronousNotificationWeight {
  Urgent = 'urgent',
  Normal = 'normal'
}

export type AsynchronousNotificationAction = ButtonIconProps & ButtonWeightProps & {
  children: ReactNode;

  tooltip?: React.ReactNode;
  isTooltipVisible?: () => boolean;

  /**
   * Should return an asynchronous function to run when clicking on the button.
   * During execution, a task indicator is rendered. The notification is closed
   * only if execution finished without any thrown error.
   * 
   * Return true to delete the notification.
   */
  onClick: (params: AsynchronousNotificationClickParams) => Promise<boolean | void> | boolean | void;
}